import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";

// Detect whether a string looks mostly like English (Latin letters).
function isLikelyEnglish(text) {
  const letters = text.replace(/[^A-Za-z\u0600-\u06FF]/g, "");
  if (letters.length < 2) return false;
  const latin = (letters.match(/[A-Za-z]/g) || []).length;
  return latin / letters.length > 0.6;
}

// Walk up the DOM to see if the selection lives inside an element we should
// ignore (inputs, buttons, the popover itself, code blocks, etc.).
function isInsideIgnored(node) {
  let el = node && node.nodeType === 1 ? node : node && node.parentElement;
  while (el) {
    if (el.dataset && el.dataset.noTranslate === "true") return true;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON" || tag === "SELECT") return true;
    if (el.getAttribute && el.getAttribute("contenteditable") === "true") return true;
    el = el.parentElement;
  }
  return false;
}

/**
 * Listens for EN→AR-eligible text selections anywhere on the page, fetches
 * the translation (cached), and returns the state needed to render a popover.
 *
 * Consumers pass in a `popRef` so we don't close when clicking inside the
 * popover itself.
 */
export function useTextSelectionTranslate(popRef) {
  const [state, setState] = useState({
    open: false, x: 0, y: 0, source: "", ar: "", loading: false, error: "",
  });
  const reqIdRef = useRef(0);
  const cacheRef = useRef(new Map());

  const close = useCallback(
    () => setState((s) => ({ ...s, open: false, ar: "", loading: false, error: "", source: "" })),
    [],
  );

  useEffect(() => {
    const handleSelection = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = (sel.toString() || "").trim();
      if (text.length < 2 || text.length > 800) return;

      const range = sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
      if (!range) return;
      if (isInsideIgnored(range.startContainer) || isInsideIgnored(range.endContainer)) return;
      if (!isLikelyEnglish(text)) return;

      const rect = range.getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) return;

      const popWidth = 340;
      const margin = 8;
      const vw = window.innerWidth;
      let x = rect.left + rect.width / 2 - popWidth / 2;
      x = Math.max(margin, Math.min(vw - popWidth - margin, x));
      const y = rect.bottom + 8 + window.scrollY;

      const cached = cacheRef.current.get(text);
      setState({ open: true, x, y, source: text, ar: cached || "", loading: !cached, error: "" });
      if (cached) return;

      const myReq = ++reqIdRef.current;
      api.post("/translate", { text })
        .then((res) => {
          if (myReq !== reqIdRef.current) return;
          const ar = res.data?.ar || "";
          if (ar) cacheRef.current.set(text, ar);
          setState((s) => (s.source === text ? { ...s, ar, loading: false, error: "" } : s));
        })
        .catch((err) => {
          if (myReq !== reqIdRef.current) return;
          const detail = err?.response?.data?.detail || "Translation unavailable";
          setState((s) => (s.source === text ? { ...s, loading: false, error: String(detail) } : s));
        });
    };

    const onMouseUp = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      setTimeout(handleSelection, 10);
    };
    const onTouchEnd = () => setTimeout(handleSelection, 10);
    const onMouseDown = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchend", onTouchEnd);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [popRef, close]);

  return { state, close };
}
